import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { WorkOrderStatus } from '../../../../enums/workOrderStatus';
import { ARIA_PX_PER_REM, COLUMN_MAX_REM, COLUMN_MIN_REM, LIST_MAX_WIDTH_RATIO } from '../constants';

const getRemPx = () => Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

// Widths live in a ref and are written straight to CSS variables (at most once per
// frame), so resizing never re-renders the columns or their rows.
export const useColumnWidths = (
  statuses: WorkOrderStatus[],
  widthsRef: React.MutableRefObject<Record<WorkOrderStatus, number>>,
) => {
  const listRef = useRef<HTMLDivElement>(null);
  const statusesRef = useRef(statuses);
  const maxTotalRemRef = useRef(Number.POSITIVE_INFINITY);
  const frameRef = useRef(0);

  const writeWidth = useCallback(
    (status: WorkOrderStatus) => {
      const list = listRef.current;
      if (!list) return;
      const rem = widthsRef.current[status];
      list.style.setProperty(`--wo-col-${status}`, `${rem}rem`);
      list
        .querySelector(`[data-resize-status="${status}"]`)
        ?.setAttribute('aria-valuenow', String(Math.round(rem * ARIA_PX_PER_REM)));
    },
    [widthsRef],
  );

  const clampWidth = useCallback(
    (status: WorkOrderStatus, rem: number) => {
      const others = statusesRef.current.reduce((sum, s) => (s === status ? sum : sum + widthsRef.current[s]), 0);
      const limit = Math.min(COLUMN_MAX_REM, maxTotalRemRef.current - others);
      return Math.max(COLUMN_MIN_REM, Math.min(rem, limit));
    },
    [widthsRef],
  );

  // Shrinks from the last column back until the total fits the width cap.
  const fitAll = useCallback(() => {
    const current = statusesRef.current;
    let excess = current.reduce((sum, s) => sum + widthsRef.current[s], 0) - maxTotalRemRef.current;
    for (let i = current.length - 1; i >= 0 && excess > 0; i -= 1) {
      const status = current[i];
      const shrink = Math.min(excess, widthsRef.current[status] - COLUMN_MIN_REM);
      widthsRef.current[status] -= shrink;
      excess -= shrink;
    }
    current.forEach(writeWidth);
  }, [widthsRef, writeWidth]);

  useLayoutEffect(() => {
    statusesRef.current = statuses;
    fitAll();
  }, [statuses, fitAll]);

  useEffect(() => {
    const container = listRef.current?.parentElement;
    if (!container) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      // Zero while the board is hidden behind Settings; fitting to it would collapse every column.
      if (!entry.contentRect.width) return;
      maxTotalRemRef.current = (entry.contentRect.width * LIST_MAX_WIDTH_RATIO) / getRemPx();
      fitAll();
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frameRef.current);
    };
  }, [fitAll]);

  const startResize = useCallback(
    (status: WorkOrderStatus, event: React.PointerEvent<HTMLElement>) => {
      const handle = event.currentTarget;
      const startX = event.clientX;
      const startRem = widthsRef.current[status];
      const remPx = getRemPx();
      let nextRem = startRem;
      handle.setPointerCapture(event.pointerId);

      const commitFrame = () => {
        frameRef.current = 0;
        widthsRef.current[status] = nextRem;
        writeWidth(status);
      };
      const handleMove = (moveEvent: PointerEvent) => {
        nextRem = clampWidth(status, startRem + (moveEvent.clientX - startX) / remPx);
        if (!frameRef.current) frameRef.current = requestAnimationFrame(commitFrame);
      };
      const handleEnd = () => {
        handle.removeEventListener('pointermove', handleMove);
        handle.removeEventListener('pointerup', handleEnd);
        handle.removeEventListener('pointercancel', handleEnd);
      };
      handle.addEventListener('pointermove', handleMove);
      handle.addEventListener('pointerup', handleEnd);
      handle.addEventListener('pointercancel', handleEnd);
    },
    [widthsRef, clampWidth, writeWidth],
  );

  const nudge = useCallback(
    (status: WorkOrderStatus, deltaRem: number) => {
      widthsRef.current[status] = clampWidth(status, widthsRef.current[status] + deltaRem);
      writeWidth(status);
    },
    [widthsRef, clampWidth, writeWidth],
  );

  return { listRef, startResize, nudge };
};
